import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { enviroment } from "../enviroment";
import { useRepositoriesContext } from "../contexts/RepositoriesContext";
import { SupplierStatus } from "../core/models/entities/SupplierStatus";
import {
  isSupplierStatusScopePath,
  planSupplierStatusPoll,
  SUPPLIER_STATUS_QUERY_KEY,
} from "../utils/functions/supplierStatusPoll";
import {
  getSupplierStatusRecheckToken,
  getSupplierStatusRouteMissingUntil,
  subscribeSupplierStatusStore,
} from "../utils/functions/supplierStatus";

// Flag REACT_APP_SUPPLIER_STATUS_ENABLED - build-time constant, so the export
// at the bottom is fixed for the lifetime of the bundle and hook order never
// varies at runtime (same idiom as useEditorUploadPendingPoll).
const statusEnabled = enviroment.supplierStatusEnabled;

// "Check now" (Nichlas 15/9): at most one manual read per this window, so
// a click storm never becomes a GET storm (the API caches its answer 30 s
// anyway, so a second read within the window would return the same status).
export const SUPPLIER_STATUS_CHECK_NOW_LOCK_MS = 15_000;

export interface SupplierStatusView {
  /** The last status read (undefined before the first answer / on error). */
  status: SupplierStatus | undefined;
  /** The last read says the supplier is degraded. */
  degraded: boolean;
  /**
   * When the next scheduled read fires (Date.now() ms): the last answer +
   * the poll interval. Null while no read is scheduled - the tab is hidden
   * (poll paused), a read is in flight, the route is remembered as missing,
   * or the query is idle out of scope with an ok status.
   */
  nextCheckAt: number | null;
  /** A read is in flight right now. */
  checking: boolean;
  /**
   * One extra read right away (the countdown restarts from its answer).
   * No-op while a read is in flight, within SUPPLIER_STATUS_CHECK_NOW_LOCK_MS
   * of the previous manual read, or while the route is remembered as missing.
   */
  checkNow: () => void;
  /** Date.now() ms until which checkNow is a no-op (0 = free). */
  checkNowLockedUntil: number;
}

const noop = (): void => undefined;

const EMPTY_VIEW: SupplierStatusView = {
  status: undefined,
  degraded: false,
  nextCheckAt: null,
  checking: false,
  checkNow: noop,
  checkNowLockedUntil: 0,
};

// document.visibilityState as an external store: the poll stops the moment
// the tab is hidden (react-query's own refetchIntervalInBackground follows
// window focus, not visibility) and resumes when it is shown again.
function subscribeVisibility(listener: () => void): () => void {
  if (typeof document === "undefined") return () => undefined;
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
}
function isDocumentHidden(): boolean {
  return (
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );
}
function isDocumentHiddenOnServer(): boolean {
  return false;
}

/**
 * The supplier-status query (contract B/C, 14/9), mounted ONCE by the
 * banner in the app shell (after the profile has loaded - PreLoader gates
 * on it):
 *  - key ["supplier-status"], queryFn = logoRepository.getSupplierStatus
 *    (1 GET, 0 supplier calls on the API side);
 *  - the first read on mount; afterwards the query is live ONLY on a scope
 *    route (/logo, /logo/create, /design/:id/editor) or while degraded,
 *    where it is re-read REACT_APP_SUPPLIER_STATUS_POLL_MS after each
 *    answer (staleTime = the same interval, so entering a scope route
 *    re-reads only a status older than that); 0 calls on other pages;
 *    nothing while the tab is hidden; refetchOnWindowFocus off;
 *  - the poll timer is the hook's own setTimeout from the last answer
 *    (dataUpdatedAt / errorUpdatedAt), NOT react-query's refetchInterval:
 *    that one is a fixed setInterval that a manual refetch does not move,
 *    so the banner's "Next check in m:ss" countdown could not be truthful
 *    with it. One timer = one truth: nextCheckAt = last answer + interval,
 *    and every answer (scheduled, "Check now", 503 re-check, re-read on
 *    return to the tab) restarts the countdown;
 *  - "Check now" (the banner's button): one extra read at once, at most
 *    one per SUPPLIER_STATUS_CHECK_NOW_LOCK_MS and never while a read is
 *    in flight;
 *  - a 404 from the route (API flag off) marks the tab's memory: the query
 *    is disabled for SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS (silent "ok"),
 *    then read once more when the status next matters (a scope route, a
 *    503 re-check) - so an API flag turned on reaches open tabs;
 *  - a 503 supplierUnavailable met by any toast-wrapped mutation bumps the
 *    re-check token (withToasts -> noteSupplierUnavailableForStatus): one
 *    immediate refetch, whatever the route.
 * Errors other than the 404 are held by react-query (status undefined,
 * banner hidden, no toast); never retried by react-query itself - out of
 * scope not even on tab visibility (a held error counts as settled in the
 * plan); the scope poll or a 503 re-check reads again.
 */
function useSupplierStatusEnabled(): SupplierStatusView {
  const { logoRepository } = useRepositoriesContext();
  const { pathname } = useLocation();
  const hidden = useSyncExternalStore(
    subscribeVisibility,
    isDocumentHidden,
    isDocumentHiddenOnServer
  );
  const routeMissingUntil = useSyncExternalStore(
    subscribeSupplierStatusStore,
    getSupplierStatusRouteMissingUntil,
    getSupplierStatusRouteMissingUntil
  );
  const routeMissing = Date.now() < routeMissingUntil;
  // The 404 memory expires with no render of its own (the query is disabled
  // meanwhile, so no react-query timer runs), hence one timeout to the
  // expiry: the re-render recomputes the plan - on a scope route that is
  // one read (a 404 again re-marks for another window), elsewhere still 0
  // calls (the silent ok is data, and out of scope the query stays off).
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!routeMissing) return undefined;
    const timer = setTimeout(
      rerender,
      Math.max(0, routeMissingUntil - Date.now()) + 1
    );
    return () => clearTimeout(timer);
  }, [routeMissing, routeMissingUntil]);
  const recheckToken = useSyncExternalStore(
    subscribeSupplierStatusStore,
    getSupplierStatusRecheckToken,
    getSupplierStatusRecheckToken
  );
  const inScope = isSupplierStatusScopePath(pathname);
  const pollMs = enviroment.supplierStatusPollMs;

  // The plan depends on the last answer (degraded / hasData / hasError),
  // which lives in the query cache: read it there BEFORE the observer runs
  // (the cache already holds a landed answer on the render it triggers),
  // and feed the plan back as options - react-query re-evaluates enabled /
  // refetchInterval on every render, so a degraded answer starts the poll
  // on the very render it arrives.
  const queryClient = useQueryClient();
  const lastState = queryClient.getQueryState<SupplierStatus>(
    SUPPLIER_STATUS_QUERY_KEY
  );
  const last = lastState?.data;
  const plan = planSupplierStatusPoll({
    routeMissing,
    hidden,
    inScope,
    degraded: last?.degraded === true,
    hasData: last !== undefined,
    hasError: lastState?.status === "error",
    pollMs,
  });

  const query = useQuery(
    SUPPLIER_STATUS_QUERY_KEY,
    () => logoRepository.getSupplierStatus(),
    {
      enabled: plan.enabled,
      // The poll is the hook's own timer below (see the doc comment).
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: pollMs,
      retry: false,
    }
  );

  // The poll timer: while the plan polls (a scope route or degraded, tab
  // visible, route not missing) and no read is in flight, the next read is
  // due `interval` after the last answer (data or error). A timer already
  // in the past (a stale answer when the plan starts polling again, e.g.
  // back on the tab) fires at once - unless react-query's own re-enable
  // read has started meanwhile (checked at fire time, never two reads).
  const { refetch } = query;
  const settledAt = Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
  const checking = query.isFetching;
  const nextCheckAt =
    plan.refetchInterval !== false && !checking && settledAt > 0
      ? settledAt + plan.refetchInterval
      : null;
  useEffect(() => {
    if (nextCheckAt === null) return undefined;
    const timer = setTimeout(
      () => {
        if (
          queryClient.getQueryState(SUPPLIER_STATUS_QUERY_KEY)?.fetchStatus ===
          "fetching"
        ) {
          return;
        }
        void refetch();
      },
      Math.max(0, nextCheckAt - Date.now())
    );
    return () => clearTimeout(timer);
  }, [nextCheckAt, refetch, queryClient]);

  // "Check now": one extra read, locked for SUPPLIER_STATUS_CHECK_NOW_LOCK_MS
  // per manual read (ref = the guard, state = what the banner renders).
  const lockRef = useRef(0);
  const [checkNowLockedUntil, setCheckNowLockedUntil] = useState(0);
  const checkNow = useCallback(() => {
    const now = Date.now();
    if (routeMissing || now < lockRef.current) return;
    if (
      queryClient.getQueryState(SUPPLIER_STATUS_QUERY_KEY)?.fetchStatus ===
      "fetching"
    ) {
      return;
    }
    lockRef.current = now + SUPPLIER_STATUS_CHECK_NOW_LOCK_MS;
    setCheckNowLockedUntil(lockRef.current);
    void refetch();
  }, [routeMissing, refetch, queryClient]);

  // The re-check token: one immediate refetch per bump (refetch ignores
  // `enabled`, so it works on any route). Token 0 = nothing requested yet.
  const seenTokenRef = useRef(recheckToken);
  useEffect(() => {
    if (recheckToken === seenTokenRef.current) return;
    seenTokenRef.current = recheckToken;
    if (routeMissing) return;
    // Same guard as the timer and "Check now": a read already in flight
    // brings the fresh answer, so the token never starts a third call
    // (react-query's refetch would otherwise cancel-and-restart it).
    if (
      queryClient.getQueryState(SUPPLIER_STATUS_QUERY_KEY)?.fetchStatus ===
      "fetching"
    ) {
      return;
    }
    void refetch();
  }, [recheckToken, routeMissing, refetch, queryClient]);

  return {
    status: query.data,
    degraded: query.data?.degraded === true,
    nextCheckAt,
    checking,
    checkNow,
    checkNowLockedUntil,
  };
}

// Flag off: no hook body at all (no query, no router read, no listeners) -
// the call site keeps one hook call either way.
function useSupplierStatusDisabled(): SupplierStatusView {
  return EMPTY_VIEW;
}

export const useSupplierStatus: () => SupplierStatusView = statusEnabled
  ? useSupplierStatusEnabled
  : useSupplierStatusDisabled;
